/* ─────────────────────────────────────────────────────────────
   gpc-reviews-showcase.js
   Upload to: Assets/   (paired with sections/gpc-reviews-showcase.liquid)

   Drives the arrow buttons on the full-width review rail. External
   rather than inline for the same reason as the configurator asset —
   an inline <script> in a section was not being executed on the live
   storefront. Without this the rail still scrolls by swipe and
   trackpad; you just lose the arrows.

   Loaded GLOBALLY from snippets/head.liquid, NOT from the section:
     <script src="{{ 'gpc-reviews-showcase.js' | asset_url }}" defer></script>
   A <script> inside the section never executes on this storefront, inline
   or external. It is never even fetched. Hence loading it globally.

   Do not move this back inline.
   ───────────────────────────────────────────────────────────── */

(function () {
  function init(rail) {
    if (rail.dataset.gpcrReady === '1') return;
    rail.dataset.gpcrReady = '1';

    var section = rail.closest('.gpcr');
    var nav = section && section.querySelector('[data-gpcr-nav]');
    if (!nav) return;
    var prev = nav.querySelector('[data-gpcr-prev]');
    var next = nav.querySelector('[data-gpcr-next]');
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    /* ── endless loop ──────────────────────────────────────
       The rail is filled with repeated copies of the reviews and the scroll
       position is quietly wrapped back into the middle copy whenever it drifts
       out of range. Because every copy is identical, the jump is invisible —
       you can keep going in either direction forever and the reviews that left
       one edge come back round at the other.

       The wrap happens once scrolling has STOPPED, not the instant the
       threshold is crossed: moving scrollLeft mid-animation cancels a smooth
       arrow scroll and sends it somewhere unintended. There is half a set of
       slack either side, so there is always room to wait. */
    var loop = rail.getAttribute('data-gpcr-loop') === 'true';
    var setW = 0;                       // width of one full copy, incl. its gap
    var idle = null;

    function jumpTo(x) {
      // scroll-behavior: smooth applies to scrollLeft too, and an animated
      // wrap would be the one thing that gives the trick away
      var was = rail.style.scrollBehavior;
      rail.style.scrollBehavior = 'auto';
      rail.scrollLeft = x;
      rail.style.scrollBehavior = was;
    }

    function addCopy(originals) {
      originals.forEach(function (el) {
        var c = el.cloneNode(true);
        c.setAttribute('aria-hidden', 'true');
        c.setAttribute('data-gpcr-clone', '');
        // clones must not be reachable by keyboard — the same review would
        // otherwise be tabbed through several times over
        c.querySelectorAll('a, button, [tabindex]').forEach(function (f) {
          f.setAttribute('tabindex', '-1');
        });
        rail.appendChild(c);
      });
    }

    function buildLoop() {
      if (!loop) return;
      var originals = Array.prototype.slice.call(rail.children);
      // one screenful of reviews has nowhere to loop to
      if (!originals.length || rail.scrollWidth - rail.clientWidth <= 4) return;

      addCopy(originals);
      setW = rail.children[originals.length].offsetLeft - rail.children[0].offsetLeft;
      if (setW <= 0) { setW = 0; return; }

      // enough copies that the viewport is always filled ahead of the wrap
      // point, however few reviews there are
      var sets = Math.ceil(rail.clientWidth / setW) + 3;
      for (var i = 2; i < sets; i++) addCopy(originals);

      jumpTo(setW);                     // start on the second copy
    }

    function wrapAround() {
      if (!setW) return;
      var x = rail.scrollLeft;
      if (x < setW * 0.5) jumpTo(x + setW);
      else if (x >= setW * 1.5) jumpTo(x - setW);
    }

    function sync() {
      var max = rail.scrollWidth - rail.clientWidth;
      var scrollable = max > 4;

      nav.hidden = !scrollable;                 // nothing to scroll to
      rail.classList.toggle('is-static', !scrollable);
      if (!scrollable) {
        rail.classList.remove('is-scrolled', 'is-end');
        return;
      }

      if (setW) {
        // Looping: there is always more in both directions, so both edges fade
        // and neither arrow is ever a dead end.
        rail.classList.add('is-scrolled');
        rail.classList.remove('is-end');
        prev.disabled = false;
        next.disabled = false;
        return;
      }

      // The edge fade means "this carries on past the screen edge", so it has
      // to track exactly the same state as the arrows. Fading an end you have
      // already reached would suggest a card is hiding there when none is.
      var atStart = rail.scrollLeft <= 2;
      var atEnd = rail.scrollLeft >= max - 2;
      rail.classList.toggle('is-scrolled', !atStart);
      rail.classList.toggle('is-end', atEnd);
      prev.disabled = atStart;
      next.disabled = atEnd;
    }

    function page(dir) {
      var card = rail.querySelector('.gpcr-card');
      var step = card ? card.getBoundingClientRect().width + 16 : rail.clientWidth * 0.8;
      rail.scrollBy({ left: dir * step, behavior: reduce ? 'auto' : 'smooth' });
    }

    prev.addEventListener('click', function () { page(-1); });
    next.addEventListener('click', function () { page(1); });
    rail.addEventListener('scroll', function () {
      window.requestAnimationFrame(sync);
      if (!setW) return;
      window.clearTimeout(idle);
      idle = window.setTimeout(wrapAround, 90);     // wrap once the scroll settles
    }, { passive: true });
    window.addEventListener('resize', sync);
    if ('ResizeObserver' in window) new ResizeObserver(sync).observe(rail);

    buildLoop();
    sync();
  }

  /* ── Trustpilot ────────────────────────────────────────
     The bootstrap in layout/theme.liquid renders widgets by scanning the DOM
     once, when it loads. This storefront injects section HTML after that scan
     has already run, so our widget is never picked up and the fallback <a> is
     all you see — which is exactly what a "broken" Trustpilot strip looks
     like. The header widget is in the page early enough to be caught; ours is
     not. So hand the element to Trustpilot ourselves, waiting for the
     bootstrap to turn up if it has not yet (it is loaded async). */
  var tpTries = 0;
  var tpPending = false;

  function initTrust() {
    var els = document.querySelectorAll('.gpcr-trust .trustpilot-widget:not([data-gpcr-tp])');
    if (!els.length) return;

    if (!window.Trustpilot || !window.Trustpilot.loadFromElement) {
      if (tpPending || tpTries++ > 50) return;      // ~10s, then give up quietly
      tpPending = true;
      window.setTimeout(function () { tpPending = false; initTrust(); }, 200);
      return;
    }

    Array.prototype.forEach.call(els, function (el) {
      el.setAttribute('data-gpcr-tp', '1');
      try { window.Trustpilot.loadFromElement(el, true); } catch (e) {}
    });
  }

  function initAll(scope) {
    var found = 0;
    (scope || document).querySelectorAll('[data-gpcr-rail]').forEach(function (el) {
      init(el);
      found++;
    });
    initTrust();          // runs in Grid layout too, where there is no rail
    return found;
  }

  // Same belt and braces as the configurator asset: try now, on load, and on
  // DOM mutation, because an inline script in the section was not running at
  // all on the live storefront.
  // Confirm which build the store is serving: type  window.GPCR_VERSION  in
  // the console. Undefined means this asset is not loaded at all — check the
  // <script> line in snippets/head.liquid.
  window.GPCR_VERSION = '2026-08-13 arrows+edge-fade+loop+trustpilot';

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }
  window.addEventListener('load', function () { initAll(); });

  // Loaded globally from head.liquid, so this runs on pages with no widget at
  // all. Watch briefly for one appearing, coalesce bursts of DOM changes into a
  // single check, and stop as soon as one is found or after 15 seconds.
  if ('MutationObserver' in window) {
    var queued = false;
    var mo = new MutationObserver(function () {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(function () {
        queued = false;
        if (initAll()) mo.disconnect();
      });
    });
    mo.observe(document.documentElement, { childList: true, subtree: true });
    window.setTimeout(function () { mo.disconnect(); }, 15000);
  }

  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
